<template>
    <VJoyHighlight class="translation-section" display-icon icon="translate">
        <VJoyWrapper
            class="translation-section__content"
            :data-testid="translationStatus.actionTestId"
            direction="row"
            align="center"
            justify="space-between"
            column-gap="1"
            row-gap="1"
        >
            <div>{{ translationStatus.message }}</div>
            <VJoyLink v-if="translationStatus.action" href="#" :text="translationStatus.buttonLabel" @click="translationStatus.action" />
        </VJoyWrapper>
    </VJoyHighlight>
</template>

<script setup lang="ts">
    import {computed, onMounted} from 'vue';
    import {storeToRefs} from 'pinia';
    import {PollingResult, usePolling, useTranslation} from '#imports';
    import {pushVJoySnackbar, VJoyHighlight, VJoyLink, VJoyWrapper} from '@maltjoy/core-vue';
    import {useAsyncTranslationStore} from '~/store/translation/asyncTranslationStore';

    const {t} = useTranslation();
    const {startPolling, isPolling} = usePolling();
    const asyncTranslationStore = useAsyncTranslationStore();
    const {hasTranslation, isTranslated, isProfileTranslationActiveForUser} = storeToRefs(asyncTranslationStore);
    const {fetchTranslations, translate, restore} = asyncTranslationStore;

    type TranslationStatus = {
        message: string;
        buttonLabel: string | undefined;
        actionTestId: string;
        action: (() => void) | (() => Promise<void>) | undefined;
    };

    const translationStatus = computed<TranslationStatus>(() => {
        if (isPolling.value) {
            return {
                message: t('profile.section.translation.in.progress'),
                buttonLabel: undefined,
                actionTestId: 'translation-in-progress',
                action: undefined,
            };
        } else if (isTranslated.value) {
            return {
                message: t('profile.section.translation.translated'),
                buttonLabel: t('profile.section.translation.cta.restore'),
                actionTestId: 'translation-enabled',
                action: () => {
                    restore();
                    pushVJoySnackbar({
                        props: {
                            message: t('profile.section.translation.success.restore'),
                            level: 'success',
                        },
                    });
                },
            };
        } else {
            return {
                message: t('profile.section.translation.original'),
                buttonLabel: t('profile.section.translation.cta.translate'),
                actionTestId: 'translation-not-enabled',
                action: async () => {
                    if (!hasTranslation.value) {
                        await startTranslationPolling();
                    } else {
                        translate();
                        pushVJoySnackbar({
                            props: {
                                message: t('profile.section.translation.success.translation'),
                                level: 'success',
                            },
                        });
                    }
                },
            };
        }
    });

    async function startTranslationPolling(): Promise<void> {
        await startPolling(
            async () => {
                await fetchTranslations();
                if (hasTranslation.value) {
                    translate();
                    pushVJoySnackbar({
                        props: {
                            message: t('profile.section.translation.success.translation'),
                            level: 'success',
                        },
                    });
                    return PollingResult.SUCCESS;
                }
                return PollingResult.FAILED;
            },
            () => {
                pushVJoySnackbar({
                    props: {
                        message: t('profile.section.translation.error'),
                        level: 'error',
                    },
                });
            },
            {delayInMs: 15_000, maxRetries: 3},
        );
    }

    onMounted(() => {
        // Start polling automatically if translation is not available but user has activated profile translations
        if (!hasTranslation.value && isProfileTranslationActiveForUser.value) {
            startTranslationPolling();
        }
    });
</script>

<style scoped lang="scss">
    .translation-section {
        margin-bottom: var(--joy-core-spacing-8);

        &__content {
            margin-bottom: var(--joy-core-spacing-0);
        }
    }
</style>
